/**
 * Reading a public document (SMA-76).
 *
 * An embed on a customer site fetches its document from the public API, which answers 404 for
 * a record that is not published and 403 `upgrade_required` when the organization's plan does
 * not include embeds (the refusal carries what the card needs). A hosted page on the disclose
 * subdomain makes no request: it inlines the same document in a JSON script tag.
 */

/** The directory's own refusals (SMA-202): its widget is off, or the page's origin is not on its allow list. */
export type DirectoryRefusal = 'widget_disabled' | 'origin_not_allowed';

export type PublicRead =
  | { status: 'ok'; document: any }
  | { status: 'unpublished' }
  | { status: 'not_embeddable'; organizationName?: string; publicUrl?: string }
  | { status: DirectoryRefusal };

export async function readPublicDocument(dataUrl: string): Promise<PublicRead> {
  const response = await fetch(dataUrl);
  if (response.status === 404) return { status: 'unpublished' };

  if (response.status === 403) {
    const body = await response.json().catch(() => null);
    const errors: any[] = body?.errors || [];
    const refusal = errors.find((e: any) => e?.code === 'upgrade_required');
    if (refusal) {
      return {
        status: 'not_embeddable',
        organizationName: refusal.meta?.organization_name,
        publicUrl: refusal.meta?.public_url,
      };
    }
    const directoryRefusal = errors.find((e: any) => e?.code === 'widget_disabled' || e?.code === 'origin_not_allowed');
    if (directoryRefusal) return { status: directoryRefusal.code as DirectoryRefusal };
  }

  if (!response.ok) throw new Error(`Failed to load (${response.status})`);

  let document: any;
  try {
    document = await response.json();
  } catch {
    throw new Error('Invalid response from server');
  }
  return { status: 'ok', document };
}

/** The document a hosted page inlines, or null when the page has none (an embed). */
export function inlineDocument(): any | null {
  const element = document.getElementById('disclosure-document');
  if (!element) return null;
  try {
    return JSON.parse(element.textContent || '');
  } catch {
    return null;
  }
}

/**
 * The widgets' shared "not available" shapes: a 404 and a refusal both surface as
 * `available: false`, told apart by `reason`.
 */
export function unavailable(read: Exclude<PublicRead, { status: 'ok' }>) {
  if (read.status === 'not_embeddable') {
    return {
      available: false as const,
      reason: 'not_embeddable' as const,
      organizationName: read.organizationName,
      publicUrl: read.publicUrl,
    };
  }
  return { available: false as const, reason: read.status };
}
